import React, { useRef, useState } from "react";
import { FormProvider, useFormState } from "react-hook-form";
import { AlertCircle, CheckCircle2, Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { Card, CardFooter } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { StepIndicator } from "@/components/ui/StepIndicator";
import StepPersonal from "./steps/StepPersonal";
import StepSkills from "./steps/StepSkills";
import StepExperience from "./steps/StepExperience";
import StepEducation from "./steps/StepEducation";
import StepSalary from "./steps/StepSalary";
import StepMoreInfo from "./steps/StepMoreInfo";
import { ALTERNATE_EMAIL_COPY } from "./steps/alternateEmailCopy";
import { Checkbox } from "@/components/ui/Checkbox";
import toast from "react-hot-toast";
import { cn } from "@/lib/utils";
import { getDefaultCurrencyFallback } from "@/hooks/useDefaultCurrency";

export const STEP_CONFIG = [
  { key: "personal_information", label: "Personal Info", component: StepPersonal },
  { key: "skills", label: "Skills & Language", component: StepSkills },
  { key: "experience", label: "Experience", component: StepExperience },
  { key: "education", label: "Education", component: StepEducation },
  { key: "salary", label: "Salary & Availability", component: StepSalary },
  { key: "more", label: "Additional Information", component: StepMoreInfo },
];

// The DOM id of a section's confirmation checkbox. Submit uses it to scroll the
// first unconfirmed section into view and focus its box.
const confirmCheckboxId = (key) => `confirm-${key}`;

// The form value(s) behind each step, in STEP_CONFIG order. One list, used for
// both jobs it has: which step turns red when a field fails validation, and
// which step's confirmation to drop when its data changes underneath.
const STEP_DATA_KEYS = [
  ["personal_information"],
  ["skills"],
  ["experience"],
  ["education"],
  ["compensation"],
  // `screening_answers` belongs to this step too: editing an answer after
  // ticking "Additional Information" has to drop that tick, the same way
  // editing any other value on the step does.
  ["additionalSections", "social", "screening_answers"],
];

// True when every value is empty/null/blank, recursing into objects and arrays.
// Keys starting with "_" (sentinels like _fallback) are skipped.
export const isDeeplyEmpty = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") return value.trim() === "";
  if (typeof value === "number" || typeof value === "boolean") return false;
  if (Array.isArray(value)) return value.every(isDeeplyEmpty);
  if (typeof value === "object") {
    return Object.entries(value).every(([k, v]) => k.startsWith("_") || isDeeplyEmpty(v));
  }
  return false;
};

/**
 * Presentational review wizard shared by the confirm-details page (data fetched
 * once) and the apply page (data streamed section-by-section while extracting).
 *
 * The react-hook-form instance is owned by the parent (via useReviewForm) and
 * passed in as `form`, so the parent can populate fields incrementally.
 *
 * @param {Object}   form            - useReviewForm() return bag
 * @param {string[]} mandatoryFields - job-config mandatory field keys
 * @param {"full"|"partial"} extractionQuality - "partial" shows the manual-fill notice
 * @param {string[]|null} readyStepKeys - step keys that are ready to edit; null = all ready
 * @param {boolean}  submitDisabled  - block submit (e.g. until extraction completes)
 * @param {boolean}  isFormSubmit    - submission in flight
 * @param {string}   submitError     - submit error message
 * @param {Function} onBack          - called when Back is pressed on the first step
 */
const ReviewWizard = ({
  form,
  mandatoryFields = [],
  extractionQuality = "full",
  readyStepKeys = null,
  colorStepKeys = null,
  submitDisabled = false,
  isFormSubmit = false,
  submitError = "",
  onBack,
  pendingNode = null,
  lockedSections = [],
  currencySign = getDefaultCurrencyFallback().currencySign,
  title = "Review your information",
  subtitle = "Extracted data from your resume. Review and correct any errors, then confirm to submit.",
  languagesSlot = null,
  // Manual screening questions for this job — passed straight to the
  // "Additional Information" step, which renders and binds them.
  screeningQuestions = [],
}) => {
  const { step, setStep, submitForm, isLastStep } = form;

  // Set once the candidate tries to submit with unconfirmed sections still
  // outstanding — drives the red "review and confirm" warning on whichever
  // unconfirmed section they're currently looking at.
  const [showConfirmHint, setShowConfirmHint] = useState(false);

  // Reactive validation state — re-renders the wizard the instant any field's
  // validity changes, so the step colours and the Submit gate stay live.
  const { errors, isValid } = useFormState({ control: form.methods.control });

  const stepsWithErrors = STEP_DATA_KEYS.map((keys) => keys.some((k) => !!errors[k]));

  // `watch()` with no argument subscribes to every field, so this component
  // re-renders whenever any value changes — including the section-by-section
  // resets the apply page performs while the resume streams in.
  const watchedValues = form.methods.watch();

  // Per-section confirmations live here, not in the form values.
  //
  // They are wizard state, not resume data: they are never submitted, and the
  // schema does not describe them. Holding them in the form meant every
  // `reset()` the apply page runs for an incoming section had to carry them,
  // which it could not — `mapApiToForm` builds resume fields only, so the whole
  // `_reviewConfirmations` object dropped out of the form on the first section
  // and RHF then kept it alive only as one "dirty" blob under keepDirtyValues.
  // One section's tick and another's were the same value to that machinery.
  //
  // A confirmation is stored as the JSON of what was confirmed. That is what
  // makes it self-expiring: when the data behind the section changes — the
  // candidate edited it, or a later streamed section overwrote it — the
  // snapshot no longer matches and the section reads as unconfirmed again,
  // which is the truth. Nothing to clear, and no effect that can miss a change.
  const [confirmedSnapshots, setConfirmedSnapshots] = useState({});

  const stepSnapshot = (index) => JSON.stringify(STEP_DATA_KEYS[index].map((k) => watchedValues?.[k] ?? null));

  const isStepConfirmed = (index) => {
    const snapshot = confirmedSnapshots[STEP_CONFIG[index].key];
    return snapshot !== undefined && snapshot === stepSnapshot(index);
  };

  const setStepConfirmed = (index, next) => {
    const { key } = STEP_CONFIG[index];
    setConfirmedSnapshots((prev) => {
      if (!next) {
        const { [key]: _dropped, ...rest } = prev;
        return rest;
      }
      return { ...prev, [key]: stepSnapshot(index) };
    });
  };

  const topRef = useRef(null);

  const isStepReady = (index) => readyStepKeys === null || readyStepKeys.includes(STEP_CONFIG[index]?.key);

  const goTo = (index) => setStep(Math.max(0, Math.min(index, STEP_CONFIG.length - 1)));

  // Jump to the first section still missing its confirmation checkbox and
  // focus it, so the candidate lands exactly where they need to act instead
  // of just reading a toast and having to hunt for the right step.
  const focusConfirmCheckbox = (key) => {
    setTimeout(() => {
      const el = document.getElementById(confirmCheckboxId(key));
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
      el?.focus();
    }, 100);
  };

  const handlePrimary = () => {
    if (isLastStep) {
      const unconfirmedSteps = STEP_CONFIG.filter((_step, i) => !isStepConfirmed(i));
      if (unconfirmedSteps.length > 0) {
        const target = unconfirmedSteps[0];
        const names = unconfirmedSteps.map((s) => s.label);
        const message =
          names.length === 1
            ? `Please confirm "${names[0]}" before submitting.`
            : `Please confirm the following sections before submitting: ${names.join(", ")}.`;
        toast.error(message);
        setShowConfirmHint(true);
        goTo(STEP_CONFIG.findIndex((s) => s.key === target.key));
        focusConfirmCheckbox(target.key);
        return;
      }
      submitForm();
    } else {
      goTo(step + 1);
    }
  };

  const handleBackClick = () => {
    if (step === 0) onBack?.();
    else goTo(step - 1);
  };

  // Free navigation — the candidate may jump to ANY step at any time. Validity is
  // enforced only at Submit, never as a navigation gate.
  const handleStepClick = (index) => goTo(index);

  const current = STEP_CONFIG[step ?? 0];
  const StepComponent = current?.component ?? STEP_CONFIG[0].component;
  const nextStepLabel = STEP_CONFIG[step + 1]?.label;
  const currentConfirmed = isStepConfirmed(step ?? 0);
  const showMissingConfirmWarning = showConfirmHint && !currentConfirmed;
  const currentReady = isStepReady(step);
  // Indicator colour uses colorStepKeys (all backing sections in) so a partially
  // streamed step (e.g. summary in, personal-info still extracting) stays amber
  // rather than flipping green/red prematurely. Falls back to readyStepKeys.
  const colorKeys = colorStepKeys ?? readyStepKeys;
  const stepsReady = colorKeys === null ? null : STEP_CONFIG.map((s) => colorKeys.includes(s.key));

  // Submit only when: caller allows it (e.g. extraction finished + ids ready),
  // the whole form passes validation, and we're not mid-submit.
  const submitBlocked = submitDisabled || !isValid || isFormSubmit;

  const NextOrSubmitButton = ({ className }) => (
    <Button onClick={handlePrimary} disabled={isLastStep ? submitBlocked : isFormSubmit} className={className}>
      {isLastStep ? (
        isFormSubmit ? (
          <>
            <Loader2 size={14} className="animate-spin" /> Submitting…
          </>
        ) : (
          "Submit"
        )
      ) : (
        <>
          <span>Next</span>
          <ChevronRight />
        </>
      )}
    </Button>
  );

  return (
    <>
      {/* Header */}
      <div className="mb-4 sm:mb-6" ref={topRef}>
        <h1 className="text-lg sm:text-lg md:text-xl font-semibold text-text-primary">{title}</h1>
        <p className="text-[11px] sm:text-xs text-text-secondary mt-1 max-w-xl">{subtitle}</p>
      </div>

      {/* Non-blocking heads-up shown only when nothing could be extracted. */}
      {extractionQuality === "partial" && (
        <div className="mb-4 rounded-lg border border-tone-amber bg-tone-amber-soft px-4 py-3 text-tone-amber">
          <p className="text-xs sm:text-sm font-medium">
            We couldn't extract any details from your resume, so the form below is empty. Please fill in your information manually before submitting.
          </p>
        </div>
      )}

      {/* Mobile: progress bar */}
      <div className="md:hidden mb-4">
        <div className="flex justify-between text-[11px] text-text-secondary mb-1.5">
          <span>
            Step {step + 1} of {STEP_CONFIG.length}
          </span>
          {nextStepLabel && <span>Next: {nextStepLabel} &raquo;</span>}
        </div>
        <div className="h-2 w-full bg-background-secondary rounded-full overflow-hidden">
          <div className="h-full bg-[#3f9d6b] rounded-full transition-all duration-300" style={{ width: `${((step + 1) / STEP_CONFIG.length) * 100}%` }} />
        </div>
      </div>

      {/* Desktop: step indicator. No `overflow-hidden` here — the rail inside
          StepIndicator does its own horizontal scrolling, and a second clipping
          box only cut the tops and bottoms of the step circles. */}
      <div className="hidden md:flex items-center">
        <StepIndicator stepsConfig={STEP_CONFIG} currentStep={step + 1} stepClick={handleStepClick} stepsWithErrors={stepsWithErrors} stepsReady={stepsReady} />
      </div>

      {/* Form Card */}
      <Card className="flex flex-col bg-background-secondary border border-border-primary !rounded-xl px-0 py-5 md:py-8 md:px-5 pb-2 mb-6 md:mb-0">
        <div className="custom-scrollbar px-3 sm:px-4 md:px-5">
          <FormProvider {...form.methods}>
            {currentReady ? (
              <>
                <StepComponent
                  mandatoryFields={mandatoryFields}
                  nextAttempt={0}
                  lockedSections={lockedSections}
                  disabled={isFormSubmit}
                  currencySign={currencySign}
                  screeningQuestions={screeningQuestions}
                  alternateEmailHelperText={ALTERNATE_EMAIL_COPY.apply.helperText}
                  languagesSlot={languagesSlot}
                />

                <div className="mt-4 mb-2 px-1">
                  {showMissingConfirmWarning && (
                    <div className="mb-2 flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-xs font-medium text-status-error">
                      <AlertCircle size={14} className="shrink-0" />
                      Please review this section and confirm it before submitting.
                    </div>
                  )}
                  <div
                    className={cn(
                      "flex items-center justify-between gap-2 rounded-lg border px-3 py-2 transition-colors",
                      currentConfirmed
                        ? "border-tone-emerald/30 bg-tone-emerald-soft"
                        : showMissingConfirmWarning
                          ? "border-red-300"
                          : "border-border-primary"
                    )}
                  >
                    {/* One checkbox, for the section on screen. It is not a form
                        field: see `confirmedSnapshots` above. */}
                    <label htmlFor={confirmCheckboxId(current.key)} className="flex flex-1 items-center gap-2 cursor-pointer select-none">
                      <Checkbox
                        id={confirmCheckboxId(current.key)}
                        checked={currentConfirmed}
                        onCheckedChange={(checked) => setStepConfirmed(step ?? 0, checked === true)}
                        disabled={isFormSubmit}
                        className={currentConfirmed ? "border-tone-emerald data-checked:border-tone-emerald data-checked:bg-tone-emerald" : ""}
                      />
                      <span className="text-sm text-text-secondary">
                        {currentConfirmed ? "Reviewed — information confirmed as accurate." : "I've reviewed this section and confirm the information is accurate."}
                      </span>
                    </label>
                    {currentConfirmed && <CheckCircle2 size={16} className="shrink-0 text-tone-emerald" />}
                  </div>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center gap-3 py-16 text-text-secondary">
                {pendingNode ?? <Loader2 size={22} className="animate-spin" />}
              </div>
            )}
          </FormProvider>
        </div>

        {/* Footer — desktop */}
        <CardFooter className="hidden md:flex p-0 pt-3 sm:pt-4 mt-auto items-end justify-end">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3 sm:gap-4 w-full">
            {submitError && <p className="text-status-error text-xs text-right">{submitError}</p>}
            <div className="flex gap-2 justify-end">
              <Button
                onClick={handleBackClick}
                disabled={isFormSubmit}
                className="flex items-center gap-2 rounded-full border border-border-primary text-xs sm:text-sm text-text-primary bg-background-tertiary hover:bg-background-tertiary/90 transition disabled:opacity-40 disabled:cursor-not-allowed py-[5px] px-5"
              >
                <ChevronLeft /> Back
              </Button>
              <NextOrSubmitButton className="flex items-center gap-2 rounded-full bg-button-secondary hover:bg-button-secondary/80 text-xs sm:text-sm text-text-base font-medium transition disabled:opacity-40 disabled:cursor-not-allowed py-[5px] px-5" />
            </div>
          </div>
        </CardFooter>
      </Card>

      {/* Footer — mobile (fixed bottom bar) */}
      <div className="fixed bottom-0 left-0 right-0 px-3 py-4 bg-background-form border-t border-border-primary md:hidden z-50">
        <div className="flex gap-2 justify-between">
          <Button
            onClick={handleBackClick}
            disabled={isFormSubmit}
            className="flex-1 flex items-center justify-center gap-2 rounded-full border border-border-primary text-xs text-text-primary bg-background-tertiary py-2"
          >
            <ChevronLeft /> Back
          </Button>
          <NextOrSubmitButton className="flex-1 flex items-center justify-center gap-2 rounded-full bg-button-secondary text-xs text-text-base py-2" />
        </div>
      </div>
    </>
  );
};

export default ReviewWizard;
